import type { UrbanNetworkEdge } from './api';
import { DUPLICATE_WARNING_THRESHOLD_M, type NewStopDraft } from './systemMapReview';

const SIN_DATO = 'Sin dato';

function fmtNum(v: number | null, decimals = 1): string {
  return v === null ? SIN_DATO : v.toFixed(decimals);
}

function DuplicateWarning({
  nearestExisting,
}: {
  nearestExisting: { id: string; name: string | null; distanceM: number };
}): JSX.Element {
  return (
    <div className="smap-detail-section smap-warning-block">
      <p className="smap-detail-section-title">Posible duplicado</p>
      <p className="smap-detail-empty">
        Hay una parada existente a {fmtNum(nearestExisting.distanceM)} m (
        {nearestExisting.name ?? nearestExisting.id}). Umbral de conveniencia ({DUPLICATE_WARNING_THRESHOLD_M} m) —
        no es un valor oficial del sistema, solo una ayuda visual para evitar altas accidentales.
      </p>
    </div>
  );
}

function DraftForm({
  draft,
  onUpdate,
}: {
  draft: NewStopDraft;
  onUpdate: (patch: Partial<NewStopDraft>) => void;
}): JSX.Element {
  return (
    <div className="smap-detail-section">
      <p className="smap-detail-section-title">Datos</p>
      <label className="smap-form-label">
        Nombre
        <input
          type="text"
          className="smap-form-input"
          value={draft.name}
          onChange={e => onUpdate({ name: e.target.value })}
          placeholder="Nombre / dirección"
        />
      </label>
      <label className="smap-form-label">
        ID municipal
        <input
          type="text"
          className="smap-form-input"
          value={draft.municipalStopId}
          onChange={e => onUpdate({ municipalStopId: e.target.value })}
          placeholder="ID municipal"
        />
      </label>
    </div>
  );
}

function DraftEdgePreview({
  effectiveEdge,
  manualEdge,
  distanceM,
  onClearManualOverride,
}: {
  effectiveEdge: UrbanNetworkEdge | null;
  manualEdge: UrbanNetworkEdge | null;
  distanceM: number | null;
  onClearManualOverride: () => void;
}): JSX.Element {
  return (
    <div className="smap-detail-section">
      <p className="smap-detail-section-title">Asociación propuesta (preview)</p>
      {effectiveEdge ? (
        <>
          <div className="smap-detail-row">
            <span className="smap-detail-row-label">Canonical edge</span>
            <span className="smap-detail-row-value">{effectiveEdge.id}</span>
          </div>
          <div className="smap-detail-row">
            <span className="smap-detail-row-label">Distancia</span>
            <span className="smap-detail-row-value">{fmtNum(distanceM)} m</span>
          </div>
          <p className="smap-detail-empty">
            {manualEdge
              ? 'Override manual — reemplazá clickeando otro segmento de Red urbana, o quitá el override.'
              : 'Propuesto automáticamente (más cercano) — clickeá otro segmento de Red urbana para cambiarlo.'}
          </p>
          {manualEdge && (
            <button type="button" className="smap-reset-btn" onClick={onClearManualOverride}>
              Quitar override manual
            </button>
          )}
        </>
      ) : (
        <p className="smap-detail-empty">
          Sin edge cercano disponible (activá "Red urbana" y clickeá un segmento para asociar).
        </p>
      )}
    </div>
  );
}

/**
 * Panel del flujo "Nueva parada" — SIEMPRE sin persistencia real: el botón
 * de guardar queda deshabilitado con un mensaje explícito, nunca un
 * no-op silencioso. Cancelar limpia el draft (memoria) sin ningún efecto
 * en la base. La parada nueva nunca recibe líneas ni filas
 * `TransitVariantStop` en este flujo — eso requeriría persistencia real,
 * que hoy no está habilitada (ver auditoría Fase A).
 */
export function SystemMapNewStopPanel({
  draft,
  proposedEdge,
  manualEdge,
  distanceM,
  nearestExisting,
  onUpdate,
  onClearManualOverride,
  onCancel,
}: {
  draft: NewStopDraft;
  proposedEdge: UrbanNetworkEdge | null;
  manualEdge: UrbanNetworkEdge | null;
  distanceM: number | null;
  nearestExisting: { id: string; name: string | null; distanceM: number } | null;
  onUpdate: (patch: Partial<NewStopDraft>) => void;
  onClearManualOverride: () => void;
  onCancel: () => void;
}): JSX.Element {
  const effectiveEdge = manualEdge ?? proposedEdge;
  const showDuplicateWarning = nearestExisting !== null && nearestExisting.distanceM < DUPLICATE_WARNING_THRESHOLD_M;

  return (
    <div className="smap-detail-panel smap-detail-panel--open">
      <div className="smap-detail-inner">
        <div className="smap-detail-header">
          <div>
            <p className="smap-detail-title">Nueva parada (provisional)</p>
            <p className="smap-detail-sub">No persistida — solo en esta sesión</p>
          </div>
          <button type="button" className="smap-detail-close" onClick={onCancel} aria-label="Cancelar">
            ✕
          </button>
        </div>

        <div className="smap-detail-section">
          <p className="smap-detail-section-title">Ubicación</p>
          <div className="smap-detail-row">
            <span className="smap-detail-row-label">Coordenadas</span>
            <span className="smap-detail-row-value">
              {draft.lat.toFixed(6)}, {draft.lon.toFixed(6)}
            </span>
          </div>
        </div>

        {showDuplicateWarning && nearestExisting && <DuplicateWarning nearestExisting={nearestExisting} />}

        <DraftForm draft={draft} onUpdate={onUpdate} />

        <DraftEdgePreview
          effectiveEdge={effectiveEdge}
          manualEdge={manualEdge}
          distanceM={distanceM}
          onClearManualOverride={onClearManualOverride}
        />

        <div className="smap-detail-section smap-persistence-block">
          <button type="button" className="smap-save-disabled" disabled>
            Guardar parada
          </button>
          <p className="smap-persistence-note">Guardado deshabilitado — pendiente de habilitar persistencia segura.</p>
          <button type="button" className="smap-reset-btn" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
